/**
 * @fileoverview Hook for managing table column visibility and ordering with localStorage persistence.
 * @module shared/hooks/useTableColumnState
 */

import { useState, useEffect, useCallback } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'

export interface UseTableColumnStateOptions<TColumnId extends string> {
  /** Unique key for localStorage persistence */
  storageKey: string
  /** Default visible columns */
  defaultVisible: Set<TColumnId>
  /** Default column order */
  defaultOrder: TColumnId[]
  /** Maximum number of visible columns (optional) */
  maxVisible?: number
}

export interface UseTableColumnStateReturn<TColumnId extends string> {
  /** Set of currently visible column IDs */
  visibleColumns: Set<TColumnId>
  /** Array of column IDs in display order */
  columnOrder: TColumnId[]
  /** Toggle visibility of a column */
  toggleColumn: (columnId: TColumnId) => void
  /** Check if a column is visible */
  isVisible: (columnId: TColumnId) => boolean
  /** Handle drag end event for column reordering */
  handleDragEnd: (event: DragEndEvent) => void
  /** Set column order directly */
  setColumnOrder: React.Dispatch<React.SetStateAction<TColumnId[]>>
  /** Set visible columns directly */
  setVisibleColumns: React.Dispatch<React.SetStateAction<Set<TColumnId>>>
  /** Number of currently visible columns */
  visibleCount: number
  /** Whether the maximum visible columns limit has been reached */
  isAtMax: boolean
  /** The maximum number of visible columns (undefined if no limit) */
  maxVisible: number | undefined
  /**
   * Make a column visible and insert it at `index` in the current order.
   * If this exceeds maxVisible, the rightmost visible column (excluding the
   * inserted one) is evicted from visibility. Order always retains all columns.
   */
  showColumnAt: (columnId: TColumnId, index: number) => void
}

/**
 * Hook for managing table column visibility and ordering with localStorage persistence.
 *
 * @param options - Configuration options
 * @returns Column state and handlers
 *
 * @example
 * ```tsx
 * type ColumnId = 'name' | 'quantity' | 'price'
 *
 * const {
 *   visibleColumns,
 *   columnOrder,
 *   toggleColumn,
 *   isVisible,
 *   handleDragEnd
 * } = useTableColumnState<ColumnId>({
 *   storageKey: 'my_table_columns',
 *   defaultVisible: new Set(['name', 'quantity']),
 *   defaultOrder: ['name', 'quantity', 'price']
 * })
 * ```
 */
export function useTableColumnState<TColumnId extends string>({
  storageKey,
  defaultVisible,
  defaultOrder,
  maxVisible,
}: UseTableColumnStateOptions<TColumnId>): UseTableColumnStateReturn<TColumnId> {
  const visibilityKey = `${storageKey}_visibility`
  const orderKey = `${storageKey}_order`

  // Initialize visibleColumns from localStorage or default
  const [visibleColumns, setVisibleColumns] = useState<Set<TColumnId>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(visibilityKey)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed)) {
            return new Set(parsed as TColumnId[])
          }
        }
      } catch (e) {
        console.error('Failed to load column visibility from localStorage', e)
      }
    }
    return defaultVisible
  })

  // Initialize columnOrder from localStorage or default
  const [columnOrder, setColumnOrder] = useState<TColumnId[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(orderKey)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed)) {
            // Ensure all default columns are present (in case of new columns added in code)
            const storedSet = new Set(parsed)
            const missing = defaultOrder.filter(col => !storedSet.has(col))
            return [...parsed, ...missing] as TColumnId[]
          }
        }
      } catch (e) {
        console.error('Failed to load column order from localStorage', e)
      }
    }
    return defaultOrder
  })

  // Persist visibleColumns to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(visibilityKey, JSON.stringify(Array.from(visibleColumns)))
      } catch (e) {
        console.error('Failed to save column visibility to localStorage', e)
      }
    }
  }, [visibleColumns, visibilityKey])

  // Persist columnOrder to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(orderKey, JSON.stringify(columnOrder))
      } catch (e) {
        console.error('Failed to save column order to localStorage', e)
      }
    }
  }, [columnOrder, orderKey])

  // Toggle column visibility
  const toggleColumn = useCallback((columnId: TColumnId): void => {
    setVisibleColumns(prev => {
      const next = new Set(prev)
      if (next.has(columnId)) {
        next.delete(columnId)
      } else {
        // Prevent adding if at max
        if (maxVisible !== undefined && prev.size >= maxVisible) {
          return prev
        }
        next.add(columnId)
      }
      return next
    })
  }, [maxVisible])

  // Check if column is visible
  const isVisible = useCallback(
    (id: TColumnId): boolean => visibleColumns.has(id),
    [visibleColumns]
  )

  // Handle drag end for column reordering
  const handleDragEnd = useCallback((event: DragEndEvent): void => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setColumnOrder(items => {
        const oldIndex = items.indexOf(active.id as TColumnId)
        const newIndex = items.indexOf(over?.id as TColumnId)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }, [])

  // Insert a column at a position in the order and make it visible,
  // evicting the rightmost visible column if over the max limit.
  const showColumnAt = useCallback(
    (columnId: TColumnId, index: number): void => {
      const oldIndex = columnOrder.indexOf(columnId)
      const without = columnOrder.filter(c => c !== columnId)
      let target = index
      if (oldIndex !== -1 && oldIndex < index) target -= 1
      target = Math.max(0, Math.min(target, without.length))
      const nextOrder = [...without.slice(0, target), columnId, ...without.slice(target)]

      const nextVisible = new Set(visibleColumns)
      nextVisible.add(columnId)
      if (maxVisible !== undefined && nextVisible.size > maxVisible) {
        const evictee = [...nextOrder]
          .reverse()
          .find(c => c !== columnId && nextVisible.has(c))
        if (evictee) nextVisible.delete(evictee)
      }

      setColumnOrder(nextOrder)
      setVisibleColumns(nextVisible)
    },
    [columnOrder, visibleColumns, maxVisible]
  )

  // Computed values for max limit
  const visibleCount = visibleColumns.size
  const isAtMax = maxVisible !== undefined && visibleCount >= maxVisible

  return {
    visibleColumns,
    columnOrder,
    toggleColumn,
    isVisible,
    handleDragEnd,
    showColumnAt,
    setColumnOrder,
    setVisibleColumns,
    visibleCount,
    isAtMax,
    maxVisible,
  }
}
